import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PlayCircle, Film, Trash2 } from 'lucide-react';
import { timeAgo } from '@/lib/utils';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export function ReplaysRail() {
  const { user } = useAuth();
  const [vods, setVods] = useState([]);
  const [active, setActive] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    api.get('/vods?limit=12').then((r) => setVods(r.data || [])).catch(() => {});
  }, []);

  const canDelete = (v) => !!user && (user.role === 'admin' || user.id === v.user_id);

  const deleteVod = async (v) => {
    if (!window.confirm('Delete this replay? This cannot be undone.')) return;
    setDeleting(v.id);
    try {
      await api.delete(`/vod/${v.id}`);
      setVods((prev) => prev.filter((x) => x.id !== v.id));
      setActive((a) => (a?.id === v.id ? null : a));
      toast.success('Replay deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete replay');
    } finally {
      setDeleting(null);
    }
  };

  if (!vods.length) return null;

  return (
    <section className="mb-4" data-testid="replays-rail">
      <div className="mb-2 flex items-center gap-2">
        <Film className="h-3.5 w-3.5 text-[#2DE2E6]" />
        <p className="text-xs font-semibold uppercase tracking-wider text-[#2DE2E6]">Replays</p>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 th-scrollbar-none">
        {vods.map((v) => (
          <div key={v.id} className="relative h-32 w-24 flex-shrink-0" data-testid={`replay-tile-${v.id}`}>
            <button
              type="button"
              onClick={() => setActive(v)}
              className="group relative h-full w-full overflow-hidden rounded-xl border border-white/10 bg-white/5 text-left transition-transform hover:scale-[1.03]"
              data-testid={`replay-card-${v.id}`}
            >
              <div className="absolute inset-0 grid place-items-center bg-gradient-to-b from-transparent to-black/70">
                <PlayCircle className="h-8 w-8 text-white/90" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-1.5">
                <p className="truncate text-[11px] font-semibold text-white">{v.user_name}</p>
                <p className="truncate text-[9px] text-white/60">{v.venue_name || 'Live replay'} · {timeAgo(v.created_at)} ago</p>
              </div>
            </button>
            {canDelete(v) && (
              <button
                type="button"
                onClick={() => deleteVod(v)}
                disabled={deleting === v.id}
                className="absolute right-1 top-1 z-10 rounded-full bg-black/70 p-1.5 text-white/80 transition-colors hover:bg-red-500/80 hover:text-white"
                aria-label="Delete replay"
                data-testid={`replay-delete-${v.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) setActive(null); }}>
        <DialogContent className="max-w-md p-0 border-white/10 bg-black text-white overflow-hidden" data-testid="replay-player-dialog">
          <DialogTitle className="sr-only">Live replay player</DialogTitle>
          {active && (
            <div>
              <video
                src={`${API_BASE}/api/vod/${active.id}/stream`}
                controls autoPlay playsInline
                className="w-full max-h-[70vh] bg-black"
                data-testid="replay-video"
              />
              <div className="flex items-center justify-between gap-3 p-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{active.user_name}</p>
                  <p className="text-xs text-white/60">{active.venue_name || 'Live replay'} · {timeAgo(active.created_at)} ago</p>
                </div>
                {canDelete(active) && (
                  <Button
                    variant="outline"
                    onClick={() => deleteVod(active)}
                    disabled={deleting === active.id}
                    className="h-9 shrink-0 rounded-lg border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/25"
                    data-testid="replay-dialog-delete-button"
                  >
                    <Trash2 className="mr-1.5 h-4 w-4" /> Delete
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
